import { branding } from '@guridosdrones/shared';
import { InstagramIcon, WhatsAppIcon } from '@guridosdrones/ui';
import { ArrowUpRight, CirclePlay, MapPin, Music2, PhoneCall } from 'lucide-react';

import { scrollToSection } from '@/utils';

function HudLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-mono text-[10px] tracking-[0.2em] text-primary/50 uppercase">
      {children}
    </span>
  );
}

export function Footer() {
  const currentYear = new Date().getFullYear();
  const phoneHref = `tel:${branding.contact.phone.replace(/[^\d+]/g, '')}`;

  const socialLinks = [
    { label: 'WhatsApp', href: branding.social.whatsapp, icon: WhatsAppIcon },
    { label: 'Instagram', href: branding.social.instagram, icon: InstagramIcon },
    { label: 'YouTube', href: branding.social.youtube, icon: CirclePlay },
    { label: 'TikTok', href: branding.social.tiktok, icon: Music2 },
  ];

  const openWhatsApp = () => {
    window.open(branding.contact.whatsapp, '_blank', 'noopener,noreferrer');
  };

  return (
    <footer className="relative mt-12 w-full overflow-hidden border-t border-border bg-muted/50 dark:border-white/10">
      {/* Subtle grid background */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-size-[48px_48px] opacity-30 dark:opacity-60" />

      {/* Glow accents */}
      <div className="pointer-events-none absolute top-0 right-0 hidden h-72 w-72 rounded-full bg-primary/5 blur-[110px] sm:block" />

      <section className="relative z-10 mx-auto max-w-7xl px-4 pt-14 pb-8 sm:px-6 sm:pt-16 sm:pb-10 lg:px-8 lg:pt-20">
        {/* CTA Card */}
        <div className="relative overflow-hidden border border-border bg-card p-6 backdrop-blur-sm dark:border-white/10 sm:p-8 lg:p-12">
          {/* Corner brackets */}
          <svg
            className="absolute top-3 left-3 h-5 w-5 text-primary/30"
            viewBox="0 0 20 20"
            fill="none"
            role="img"
            aria-label="Decoração"
          >
            <path d="M0 6V0h6" stroke="currentColor" strokeWidth="1" />
          </svg>
          <svg
            className="absolute top-3 right-3 h-5 w-5 text-primary/30"
            viewBox="0 0 20 20"
            fill="none"
            role="img"
            aria-label="Decoração"
          >
            <path d="M20 6V0h-6" stroke="currentColor" strokeWidth="1" />
          </svg>
          <svg
            className="absolute bottom-3 left-3 h-5 w-5 text-primary/30"
            viewBox="0 0 20 20"
            fill="none"
            role="img"
            aria-label="Decoração"
          >
            <path d="M0 14v6h6" stroke="currentColor" strokeWidth="1" />
          </svg>
          <svg
            className="absolute bottom-3 right-3 h-5 w-5 text-primary/30"
            viewBox="0 0 20 20"
            fill="none"
            role="img"
            aria-label="Decoração"
          >
            <path d="M20 14v6h-6" stroke="currentColor" strokeWidth="1" />
          </svg>

          <div className="relative grid gap-6 sm:gap-8 lg:grid-cols-[1.25fr_auto] lg:items-end">
            <div className="max-w-3xl">
              <p className="mb-4 font-mono text-xs tracking-[0.25em] text-primary/70 uppercase">
                Próxima turma aberta
              </p>
              <h2 className="text-2xl leading-tight font-bold text-foreground font-heading tracking-tight sm:text-4xl lg:text-5xl">
                Pronto para operar drones com segurança e lucratividade no campo?
              </h2>
              <p className="mt-4 max-w-2xl text-sm text-muted-foreground sm:text-base lg:text-lg">
                Formação completa em pulverização e mapeamento 3D, com método aplicado para quem
                quer resultado real no agronegócio.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
              <button
                type="button"
                onClick={openWhatsApp}
                className="group inline-flex h-12 w-full items-center justify-center gap-2 border border-primary/40 bg-primary/10 px-6 font-mono text-sm font-bold tracking-widest text-primary uppercase transition-all hover:border-primary/70 hover:bg-primary/20 sm:w-auto sm:px-7"
              >
                Matricule-se Agora
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </button>
              <button
                type="button"
                onClick={() => scrollToSection('#contact')}
                className="inline-flex h-12 w-full items-center justify-center border border-border bg-muted/50 px-6 font-mono text-sm font-medium tracking-wide text-muted-foreground uppercase transition-all hover:border-border hover:text-foreground dark:border-white/10 dark:bg-white/5 dark:hover:border-white/20 dark:hover:text-white/70 sm:w-auto sm:px-7"
              >
                Tirar Dúvidas
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-7xl px-4 pb-8 sm:px-6 sm:pb-10 lg:px-8">
        <div className="grid gap-8 border-y border-border py-10 dark:border-white/10 sm:gap-10 sm:py-12 lg:grid-cols-[1.35fr_1fr]">
          <div>
            <HudLabel>Ground Station</HudLabel>
            <h3 className="mt-2 text-2xl font-heading font-black tracking-tight text-foreground sm:text-3xl">
              <span className="uppercase">{branding.brandNamePart1}</span>{' '}
              <span className="uppercase text-primary">{branding.brandNamePart2}</span>
            </h3>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
              Referência nacional em drones de pulverização, com foco em formação técnica de alta
              performance para o agro.
            </p>
            <div className="mt-6 hidden items-center gap-2 border border-border bg-muted/50 px-4 py-2 font-mono text-[10px] tracking-[0.15em] text-muted-foreground uppercase dark:border-white/10 dark:bg-white/5 sm:inline-flex">
              <MapPin className="h-3.5 w-3.5 text-primary/60" />
              {branding.contact.address.city} - {branding.contact.address.state} &middot; -31.7654
              S, -52.3376 W
            </div>
          </div>

          <div className="min-w-0">
            <HudLabel>Canais de Contato</HudLabel>
            <ul className="mt-5 flex flex-col gap-3 text-sm text-muted-foreground">
              <li>
                <a
                  href={phoneHref}
                  className="inline-flex items-center gap-2 transition-colors hover:text-primary"
                >
                  <PhoneCall className="h-4 w-4 shrink-0" />
                  {branding.contact.phone}
                </a>
              </li>
            </ul>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="group flex h-10 w-10 items-center justify-center border border-border bg-muted/50 text-muted-foreground transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:bg-primary/10 hover:text-primary dark:border-white/10 dark:bg-white/5"
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 pt-6 text-center font-mono text-[10px] tracking-wider text-muted-foreground/50 uppercase sm:pt-8 md:flex-row md:text-left">
          <a
            href="https://attodev.com.br"
            className="inline-flex items-center gap-2 transition-colors hover:text-muted-foreground"
            aria-label="Feito pela AttoDev"
          >
            <span>Feito pela</span>
            <span className="flex h-4 w-[82px] items-center">
              <img
                src="/logo/attodev-logo-accent.svg"
                alt="AttoDev"
                className="h-full w-full object-contain dark:hidden"
                loading="lazy"
                decoding="async"
              />
              <img
                src="/logo/attodev-logo-dark.svg"
                alt="AttoDev"
                className="hidden h-full w-full object-contain dark:block"
                loading="lazy"
                decoding="async"
              />
            </span>
          </a>
          <p>
            © {currentYear} {branding.brandName}. Todos os direitos reservados.
          </p>
        </div>
      </section>
    </footer>
  );
}
